// Piso 1 de la Sala de Control, montado como el panel de Calypso del CRM de Santi (01-10, Pau: «una
// sola gráfica en toda la pantalla no me gusta»): ventas netas del día en grande con los avisos al
// lado, las 6 métricas de Pau frente a ayer y la semana pasada, una franja baja con la gráfica del día
// y siete pequeñas de cada hora frente a ayer (22-10: + conversión, $ por fan y $ por mensaje), y debajo la tabla de cuentas (ordenable), que es lo
// que llena la pantalla. Poco texto: la definición de cada métrica va en su «i».
// 08-10-2026 (Pau: «filtrar por fechas mucho más personalizado» y «fans únicos chateados»): arriba, el selector de
// fechas; «Hoy» es esta vista (con la lectura de hoy, lo normal a esta hora y los fans) y cualquier otro periodo
// se ve en PulsoRango, con la historia guardada.
import { Info } from 'lucide-react';
import { useMemo, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { Aviso, PistaAyuda } from '../ui';
import { diaConSemana, isoDe, masDias, rotulo, seleccionDe, type Seleccion } from './fechas';
import { GraficoAcumulado } from './GraficoAcumulado';
import { BarrasDia, HoverDia, LineaDia } from './GraficosDia';
import { delta, dinero, dineroExacto, EN_PUNTOS, horaUtc, miles, numero, SIN_VALOR, SUBIR_ES_BUENO, tramosDeHoras, valorMetrica, type Delta } from './formato';
import { Exacto, FOCO } from './Puntero';
import { MiniHora } from './MiniHora';
import { PulsoRango, textoComparacion } from './PulsoRango';
import { SelectorFechas } from './SelectorFechas';
import { TablaCuentas } from './TablaCuentas';
import type { ClaveMetrica, CuentasSala, DiaSala, MetricaSala, ModeloRangoSala, ModelosSala, NormalHora, PulsoSala, RangoModelosSala, RangoSala, VistaModeloSala } from './tipos';
import { nombreFamilia } from './modelos';
import { useSalaRango, useSalaRangoModelos } from './useSalaRango';
import { useSalaRincon } from './useSalaRincon';
import { useTamano } from './useTamano';

const TONO: Record<Delta['tono'], string> = {
  bueno: 'text-oficina-trabajando',
  malo: 'text-oficina-error',
  neutro: 'text-oficina-texto3',
};

/** `junto`: la flecha pegada a la cifra («↓0,9 pts»), para las tarjetas compactas. */
function Comparacion({ d, className, junto = false }: { d: Delta | null; className?: string; junto?: boolean }) {
  if (!d) return <span className={cn('text-oficina-texto3', className)}>{SIN_VALOR}</span>;
  if (d.direccion === 'igual') return <span className={cn('text-oficina-texto3', className)}>{t('salaControl.igual')}</span>;
  return <span className={cn(TONO[d.tono], className)}>{`${d.direccion === 'sube' ? '↑' : '↓'}${junto ? '' : ' '}${d.texto}`}</span>;
}

export interface DisposicionPulso {
  columnas: 2 | 3 | 6;
  /** Tarjetas de métrica estrechas (seis en una fila de menos de ~1080 px): letra y rótulos más cortos. */
  compacta: boolean;
}

/** Cómo van las seis métricas según el ancho real del Pulso (medido: la ventana de la sala no mide lo que la pantalla,
 *  depende de si la lista del equipo está abierta). 10-10 (Pau): a ~830 px iban de 3 en 3 y no se veía ninguna gráfica
 *  sin bajar. null sin medir (tests, primer pintado): se queda con las clases por ancho de pantalla. */
export function disposicionPulso(ancho: number | null): DisposicionPulso | null {
  if (ancho === null) return null;
  if (ancho >= 760) return { columnas: 6, compacta: ancho < 1080 };
  return { columnas: ancho >= 480 ? 3 : 2, compacta: false };
}
const COLUMNAS: Record<DisposicionPulso['columnas'], string> = { 2: 'grid-cols-2', 3: 'grid-cols-3', 6: 'grid-cols-6' };

function Avisos({ pulso, atenuar }: { pulso: PulsoSala; atenuar: boolean }) {
  return (
    <div className={cn('flex flex-wrap justify-end gap-2', atenuar && 'opacity-40')}>
      {pulso.avisos.length ? (
        pulso.avisos.map((a) => (
          <span key={a.texto} className="flex items-center gap-2 rounded-full bg-oficina-elevada px-3.5 py-1.5 text-o-sub">
            <span className={cn('h-2 w-2 rounded-full', a.nivel === 'rojo' ? 'bg-oficina-error' : 'bg-oficina-bloqueado')} />
            {a.texto}
          </span>
        ))
      ) : (
        <span className="flex items-center gap-2 rounded-full bg-oficina-elevada px-3.5 py-1.5 text-o-sub text-oficina-texto2">
          <span className="h-2 w-2 rounded-full bg-oficina-trabajando" />
          {t('salaControl.sinAvisos')}
        </span>
      )}
    </div>
  );
}

/** 10-10 (Pau): antes iba en una cajita con scroll propio que enseñaba unas 4 filas; ahora a su altura, 10 a la vista
 *  y «Ver las 25». */
const CUENTAS_A_LA_VISTA = 10;

function Cuentas() {
  const { foto, fallo } = useSalaRincon<CuentasSala>('cuentas');
  const top = foto?.datos?.top ?? null;
  return (
    <div className="flex flex-col rounded-o-m bg-oficina-elevada px-2 pb-2 pt-3">
      <div className="mb-1 flex flex-wrap items-baseline gap-x-3 px-2">
        <h3 className="text-o-cuerpo font-semibold">{t('salaControl.tabla.titulo')}</h3>
        {top?.length ? <span className="text-o-nota text-oficina-texto3">{t('salaControl.tabla.sub', { n: numero(top.length) })}</span> : null}
      </div>
      {top?.length ? (
        <TablaCuentas cuentas={top} aLaVista={CUENTAS_A_LA_VISTA} />
      ) : (
        <p className="px-2 py-4 text-o-texto text-oficina-texto3">{foto || fallo ? t('salaControl.tabla.sinDatos') : t('salaControl.tabla.cargando')}</p>
      )}
    </div>
  );
}

/** «Dentro / fuera de lo normal a esta hora», con su franja. */
function NormalAEstaHora({ valor, normal, formato, compacta }: { valor: number | null; normal: NormalHora | undefined; formato: (v: number) => string; compacta: boolean }) {
  if (!normal || valor === null) return null;
  const dentro = valor >= normal.bajo && valor <= normal.alto;
  return (
    <div className="mt-2 border-t border-oficina-linea pt-1.5 text-o-mini text-oficina-texto3">
      <div className="flex justify-between gap-2">
        {compacta ? <span title={t('salaControl.hoyExtra.normal')}>{t('salaControl.hoyExtra.normalCorto')}</span> : <span>{t('salaControl.hoyExtra.normal')}</span>}
        {/* 22-10 (revisión de la sala): «dentro» en gris, no en verde: seis etiquetas verdes no decían nada y tapaban lo rojo. */}
        <span className={dentro ? 'text-oficina-texto3' : 'font-medium text-oficina-error'}>{t(dentro ? 'salaControl.hoyExtra.dentro' : 'salaControl.hoyExtra.fuera')}</span>
      </div>
      <span className="whitespace-nowrap tabular-nums">{`${formato(normal.bajo)} – ${formato(normal.alto)}`}</span>
    </div>
  );
}

/** Fans de hoy: cuántos llevan chateado frente a ayer y la semana pasada a esta hora, su curva, los de cada día
 * de los últimos 30 días y los distintos de 7 y 30 días (que nunca son la suma de los días). */
function FansHoy({ pulso, referencia, hasta, r30, r7, alAbrirDia }: { pulso: PulsoSala; referencia: Date; hasta: string; r30: RangoSala | null; r7: RangoSala | null; alAbrirDia: (d: string) => void }) {
  const f = pulso.fans;
  const ac = pulso.fansAcumulado;
  const dia = diaConSemana(masDias(isoDe(referencia.getTime()), -7)).split(' ')[0]!;
  return (
    <>
      <h3 className="mt-2 px-1 text-o-nota font-semibold text-oficina-texto2">{t('salaControl.rango.fansTitulo')}</h3>
      <div className="grid grid-cols-1 gap-2.5 xl:grid-cols-[4fr_5fr_3fr]">
        <div className="flex min-h-[240px] flex-col rounded-o-m bg-oficina-elevada px-4 pb-2 pt-3">
          <h3 className="text-o-texto font-semibold">{t('salaControl.hoyExtra.fansHoy')}</h3>
          <p className="text-o-nota text-oficina-texto3">{t('salaControl.hoyExtra.fansHoySub')}</p>
          <p className="mt-2 text-o-nota text-oficina-texto2">{t('salaControl.hoyExtra.hastaAhora', { hora: hasta })}</p>
          <p className="text-o-cifra font-bold leading-tight tracking-tight">{f ? numero(f.hoy.chatean) : SIN_VALOR}</p>
          {f ? (
            <p className="text-o-nota text-oficina-texto3">
              {t('salaControl.hoyExtra.ayerAEstaHora', { n: numero(f.ayer.chatean) })} <Comparacion d={delta(f.hoy.chatean, f.ayer.chatean, false)} />
              <br />
              {t('salaControl.hoyExtra.semanaAEstaHora', { n: numero(f.sem.chatean), dia })} <Comparacion d={delta(f.hoy.chatean, f.sem.chatean, false)} />
            </p>
          ) : null}
          {ac ? (
            <div className="mt-1 min-h-[110px] flex-1">
              <GraficoAcumulado acumulado={ac} horaHoy={pulso.hora_utc} referencia={referencia} etiqueta={t('salaControl.hoyExtra.fansHoy')} formatoEje={(v) => `${miles(v)} k`} formato={(v) => numero(v)} />
            </div>
          ) : null}
        </div>
        <div className="flex min-h-[240px] flex-col rounded-o-m bg-oficina-elevada px-4 pb-2 pt-3">
          <h3 className="text-o-texto font-semibold">{t('salaControl.hoyExtra.ultimos30')}</h3>
          <p className="text-o-nota text-oficina-texto3">{t('salaControl.rango.fansDiaSub')}</p>
          <div className="min-h-[180px] flex-1">
            {r30 ? (
              <HoverDia>
                <BarrasDia
                  dias={r30.serie.map((x) => x.dia)}
                  valores={r30.serie.map((x) => x.fans)}
                  banda={r30.bandas.fans}
                  formato={(v) => numero(v)}
                  formatoEje={(v) => `${miles(v)} k`}
                  etiqueta={t('salaControl.hoyExtra.ultimos30')}
                  conMedia={false}
                  hoyRayado={f?.hoy.chatean ?? null}
                  alSeleccionar={alAbrirDia}
                />
              </HoverDia>
            ) : (
              <p className="py-8 text-center text-o-sub text-oficina-texto3">{t('salaControl.fechas.cargando')}</p>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2.5 rounded-o-m bg-oficina-elevada px-4 pb-3 pt-3">
          <h3 className="text-o-texto font-semibold">{t('salaControl.rango.distintos')}</h3>
          <p className="-mt-2 text-o-nota text-oficina-texto3">{t('salaControl.rango.distintosSub')}</p>
          {[
            { r: r7, titulo: t('salaControl.hoyExtra.distintos7') },
            { r: r30, titulo: t('salaControl.hoyExtra.distintos30') },
          ].map(({ r, titulo }) => (
            <div key={titulo} className="rounded-o-s bg-oficina-panel px-3 py-2">
              <p className="text-o-nota text-oficina-texto3">{`${titulo} · ${r ? `${r.desde.slice(8)}/${r.desde.slice(5, 7)}–${r.hasta.slice(8)}/${r.hasta.slice(5, 7)}` : ''}`}</p>
              <p className="text-o-titulo font-bold tracking-tight">{r ? numero(r.fans.distintos) : SIN_VALOR}</p>
              {r?.a ? (
                <p className="text-o-mini text-oficina-texto3">
                  <Comparacion d={delta(r.total.fans, r.a.fans, false)} /> {`(${numero(r.a.fans)})`}
                </p>
              ) : null}
            </div>
          ))}
          {r7 && r7.fans.distintos > 0 ? (
            <p className="text-o-mini leading-snug text-oficina-texto3">{t('salaControl.hoyExtra.sumaBarras', { suma: numero(r7.fans.sumaDias), n: numero(r7.fans.sumaDias / r7.fans.distintos, 1) })}</p>
          ) : null}
        </div>
      </div>
    </>
  );
}

const FORMATO_NORMAL: Record<ClaveMetrica, (v: number) => string> = {
  unlock: (v) => `${numero(v, 1)} %`,
  cvr: (v) => `${numero(v, 1)} %`,
  golden: (v) => `${numero(v, 1)} %`,
  reply: (v) => `${numero(v)} s`,
  por_fan: (v) => dinero(v, 2),
  por_msg: (v) => dinero(v, 3),
};

export function Pulso({ pulso, atenuarAvisos }: { pulso: PulsoSala; atenuarAvisos: boolean }) {
  const referencia = pulso.generado_at ? new Date(pulso.generado_at) : new Date();
  const hoy = isoDe(referencia.getTime());
  const [seleccion, setSeleccion] = useState<Seleccion>(() => seleccionDe('hoy', hoy));
  // Al cambiar el día UTC con la sala abierta en «Hoy», «Hoy» sigue siendo hoy.
  const sel = seleccion.atajo === 'hoy' && seleccion.desde !== hoy ? seleccionDe('hoy', hoy) : seleccion;
  const esHoy = sel.atajo === 'hoy';
  const elegido = useSalaRango(esHoy ? null : sel.desde, esHoy ? null : sel.hasta);
  // En «Hoy»: los últimos 30 y 7 días cerrados (fans de cada día y distintos).
  const ult30 = seleccionDe('30', hoy);
  const ult7 = seleccionDe('7', hoy);
  const r30 = useSalaRango(esHoy ? ult30.desde : null, esHoy ? ult30.hasta : null).rango;
  const r7 = useSalaRango(esHoy ? ult7.desde : null, esHoy ? ult7.hasta : null).rango;
  const hasta = pulso.hora_utc !== null ? horaUtc(pulso.hora_utc, referencia) : SIN_VALOR;
  const raros = useMemo(() => {
    const s = new Set<string>();
    for (const r of [elegido.rango, r30]) for (const m of r?.momentos ?? []) for (const c of m.cifras) s.add(c.dia);
    return s;
  }, [elegido.rango, r30]);
  const abrirDia = (d: string) => setSeleccion({ atajo: d === masDias(hoy, -1) ? 'ayer' : 'fechas', desde: d, hasta: d });
  const semanaPasada = diaConSemana(masDias(hoy, -7)).split(' ')[0]!;
  const [medida, tam] = useTamano<HTMLDivElement>();
  const disposicion = disposicionPulso(tam?.ancho ?? null);
  // 22-10 (Pau: «cuando cambio de fecha se quita el filtro»): el modelo elegido vive aquí, por encima de «Hoy» y de las
  // fechas. Hoy, los modelos que contestan ahora (rincón «modelos»); un periodo cerrado, los de la historia por modelo.
  const [modeloElegido, setModeloElegido] = useState<string | null>(null);
  const vivos = useSalaRincon<ModelosSala>('modelos').foto?.datos ?? null;
  const rm = useSalaRangoModelos(esHoy ? null : sel.desde, esHoy ? null : sel.hasta);
  const listaModelos = esHoy ? (vivos?.modelos ?? []) : (rm.rango?.modelos ?? []);
  const vm = esHoy ? (vivos?.modelos.find((m) => m.modelo === modeloElegido) ?? null) : null;
  const mr = esHoy ? null : (rm.rango?.modelos.find((m) => m.modelo === modeloElegido) ?? null);
  const paradas = pulso.paradas ? { hoy: pulso.paradas.hoy, ayer: pulso.paradas.ayer } : undefined;
  return (
    <div ref={medida} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <SelectorFechas seleccion={sel} hoy={hoy} horaHoy={hasta} raros={raros} alCambiar={setSeleccion} />
        <Avisos pulso={pulso} atenuar={atenuarAvisos} />
      </div>
      <p className="-mt-1 px-1 text-o-nota text-oficina-texto3">
        {esHoy ? t('salaControl.fechas.comparado.hoy', { semana: semanaPasada }) : elegido.rango ? textoComparacion(elegido.rango) : ''}
        {!esHoy && sel.hasta === masDias(hoy, -1) && sel.desde !== sel.hasta ? <span className="ml-3 text-oficina-te-necesita-claro">{t('salaControl.fechas.hoyFuera')}</span> : null}
      </p>
      {listaModelos.length >= 2 ? (
        <SelectorModelo modelos={listaModelos} elegido={vm?.modelo ?? mr?.modelo ?? (!esHoy && rm.cargando ? modeloElegido : null)} alElegir={setModeloElegido} />
      ) : null}
      {vm && vivos ? (
        <VistaModelo vm={vm} horaUtc={vivos.hora_utc} referencia={referencia} disposicion={disposicion} paradas={paradas} />
      ) : esHoy ? (
        <VistaTodos pulso={pulso} referencia={referencia} hasta={hasta} r30={r30} r7={r7} alAbrirDia={abrirDia} disposicion={disposicion} />
      ) : mr && rm.rango ? (
        <VistaModeloRango m={mr} r={rm.rango} referencia={referencia} disposicion={disposicion} />
      ) : modeloElegido && rm.cargando ? (
        <p className="py-10 text-center text-o-texto text-oficina-texto3">{t('salaControl.fechas.cargando')}</p>
      ) : elegido.rango ? (
        <PulsoRango r={elegido.rango} alElegir={setSeleccion} />
      ) : elegido.fallo ? (
        <Aviso tono="error">{t('salaControl.fechas.error')}</Aviso>
      ) : (
        <p className="py-10 text-center text-o-texto text-oficina-texto3">{t('salaControl.fechas.cargando')}</p>
      )}
    </div>
  );
}

/** Las seis métricas en tarjetas: hoy, frente a ayer (y a la semana pasada si `conSemana`) y lo normal a esta hora. */
function TarjetasMetricas({
  metricas,
  normal,
  disposicion,
  conSemana = true,
  etiquetaComparacion = t('salaControl.ayer'),
}: {
  metricas: MetricaSala[];
  normal: Partial<Record<ClaveMetrica, NormalHora>>;
  disposicion: DisposicionPulso | null;
  conSemana?: boolean;
  /** Con qué se compara la cifra («ayer»; en un periodo, «anterior»). */
  etiquetaComparacion?: string;
}) {
  const compacta = disposicion?.compacta ?? false;
  return (
      <div className={cn('grid', disposicion ? COLUMNAS[disposicion.columnas] : 'grid-cols-3 xl:grid-cols-6', compacta ? 'gap-2' : 'gap-2.5')}>
        {metricas.map((m) => (
          <div key={m.clave} className={cn('min-w-0 rounded-o-m bg-oficina-elevada pb-2.5', compacta ? 'px-2.5 pt-2.5' : 'px-3.5 pt-3')}>
            <div className={cn('flex items-center whitespace-nowrap text-oficina-texto2', compacta ? 'gap-1 text-o-nota' : 'gap-1.5 text-o-sub')}>
              {t(`salaControl.metricas.${m.clave}.nombre`)}
              <PistaAyuda texto={t(`salaControl.metricas.${m.clave}.ayuda`)}>
                <button
                  type="button"
                  aria-label={t(`salaControl.metricas.${m.clave}.ayuda`)}
                  className="relative inline-flex h-4 items-center text-oficina-texto3 after:absolute after:-inset-3 hover:text-oficina-texto"
                >
                  <Info className="h-3.5 w-3.5" aria-hidden />
                </button>
              </PistaAyuda>
            </div>
            <p className={cn('font-semibold tracking-tight', compacta ? 'my-1 whitespace-nowrap text-o-titulo2' : 'my-1.5 text-o-titulo')}>{valorMetrica(m.clave, m.hoy)}</p>
            <p className="whitespace-nowrap text-o-nota leading-relaxed text-oficina-texto3">
              {etiquetaComparacion} <Comparacion d={delta(m.hoy, m.ayer, EN_PUNTOS[m.clave], SUBIR_ES_BUENO[m.clave])} junto={compacta} />
              {conSemana ? (
                <>
                  <br />
                  {compacta ? <span title={t('salaControl.semana')}>{t('salaControl.semanaCorta')}</span> : t('salaControl.semana')}{' '}
                  <Comparacion d={delta(m.hoy, m.sem, EN_PUNTOS[m.clave], SUBIR_ES_BUENO[m.clave])} junto={compacta} />
                </>
              ) : null}
            </p>
            {m.ayerDia != null ? (
              <p data-testid={`dia-entero-${m.clave}`} className="whitespace-nowrap text-o-nota text-oficina-texto3" title={t('salaControl.diaEntero.ayuda')}>
                {t(compacta ? 'salaControl.diaEntero.corto' : 'salaControl.diaEntero.largo', { valor: FORMATO_NORMAL[m.clave](m.ayerDia) })}
              </p>
            ) : null}
            <NormalAEstaHora valor={m.hoy} normal={normal[m.clave]} formato={FORMATO_NORMAL[m.clave]} compacta={compacta} />
          </div>
        ))}
      </div>
  );
}

/** La gráfica del día acumulado y las siete pequeñas de cada hora frente a ayer. */
function HorasDelDia({
  acumulado,
  horaHoy,
  ph,
  referencia,
  paradas,
  disposicion,
  conSemana = true,
  prevision,
  titulo = t('salaControl.porHora.titulo'),
}: {
  acumulado: Record<DiaSala, number[]>;
  horaHoy: number | null;
  ph: PulsoSala['porHora'];
  referencia: Date;
  paradas?: { hoy: number[]; ayer: number[] };
  disposicion: DisposicionPulso | null;
  conSemana?: boolean;
  /** Ventas esperadas de hoy a este ritmo (historia): se dibujan en la acumulada. */
  prevision?: { total: number; bajo: number; alto: number } | null;
  /** Rótulo de la leyenda («Hora a hora, hoy frente a ayer»; un día pasado, frente a su día anterior). */
  titulo?: string;
}) {
  const paradasHora = paradas;
  return (
    <>
      {/* 22-10 (revisión de la sala): una leyenda para las siete gráficas por hora (antes «última hora cerrada» repetido
          siete veces y ninguna decía qué línea era hoy y cuál ayer). */}
      <div data-testid="leyenda-horas" className="-mb-1 flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-o-nota text-oficina-texto2">
        <span className="font-semibold">{titulo}</span>
        <span className="flex items-center gap-1.5"><i className="inline-block h-[3px] w-3.5 rounded bg-oficina-te-necesita" />{t('salaControl.hoy')}</span>
        <span className="flex items-center gap-1.5"><i className="inline-block h-[2px] w-3.5 rounded bg-oficina-texto3" />{t('salaControl.ayerLeyenda')}</span>
        {paradas && (paradas.hoy.length || paradas.ayer.length) ? (
          <span className="flex items-center gap-1.5"><i className="inline-block h-3 w-3.5 rounded-sm bg-oficina-error/20" />{t('salaControl.parado.valor')}</span>
        ) : null}
        <span className="text-oficina-texto3">{t('salaControl.porHora.leyendaPie')}</span>
      </div>
      <div className="grid shrink-0 grid-cols-1 gap-2.5 xl:h-[250px] xl:grid-cols-[5fr_7fr]">
        <div className="flex min-h-[220px] flex-col rounded-o-m bg-oficina-elevada px-4 pb-2 pt-3">
          <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-o-texto font-semibold">{t('salaControl.acumulado')}</h3>
            <div className="flex gap-3 text-o-nota text-oficina-texto2">
              <span className="flex items-center gap-1.5"><i className="inline-block h-[3px] w-3.5 rounded bg-oficina-te-necesita" />{t('salaControl.hoy')}</span>
              <span className="flex items-center gap-1.5"><i className="inline-block h-[3px] w-3.5 rounded bg-oficina-texto3" />{t('salaControl.ayerLeyenda')}</span>
              {conSemana ? <span className="flex items-center gap-1.5"><i className="inline-block h-[3px] w-3.5 rounded bg-oficina-esperando" />{t('salaControl.semanaLeyenda')}</span> : null}
              {prevision ? <span className="flex items-center gap-1.5"><i className="inline-block w-3.5 border-t-2 border-dotted border-oficina-te-necesita" />{t('salaControl.esperado.leyenda')}</span> : null}
            </div>
          </div>
          <div className="min-h-0 flex-1">
            <GraficoAcumulado acumulado={acumulado} horaHoy={horaHoy} prevision={prevision} referencia={referencia} etiqueta={t('salaControl.acumulado')} />
          </div>
        </div>
        <div className="grid min-h-[220px] grid-cols-2 grid-rows-2 gap-2.5">
          <MiniHora titulo={t('salaControl.porHora.ventas')} hoy={ph.ventas.hoy} ayer={ph.ventas.ayer} formato={(x) => dinero(x)} enPuntos={false} referencia={referencia} paradas={paradasHora} />
          <MiniHora titulo={t('salaControl.porHora.unlock')} hoy={ph.unlock.hoy} ayer={ph.unlock.ayer} formato={(x) => `${numero(x, 1)} %`} enPuntos referencia={referencia} paradas={paradasHora} />
          <MiniHora titulo={t('salaControl.porHora.pagan')} hoy={ph.pagan.hoy} ayer={ph.pagan.ayer} formato={(x) => numero(x)} enPuntos={false} referencia={referencia} paradas={paradasHora} />
          <MiniHora titulo={t('salaControl.porHora.reply')} hoy={ph.reply.hoy} ayer={ph.reply.ayer} formato={(x) => `${numero(x)} s`} enPuntos={false} subirEsBueno={false} referencia={referencia} paradas={paradasHora} />
        </div>
      </div>

      {/* 22-10 (Pau): conversión y dinero por fan y por mensaje, hora a hora, en el mismo formato y a la
          altura de una gráfica pequeña de arriba; mismo orden que las tarjetas. De tres en fila con las
          métricas en una fila (Pulso ≥ 760 px; las estrechas rotulan menos horas); si no, una bajo otra.
          (22-10, captura de Pau: exigir además tarjetas no compactas las apilaba a ~1030 px, estiradas.) */}
      <div
        data-testid="pulso-horas-eficiencia"
        className={cn('grid shrink-0 auto-rows-[120px] gap-2.5', disposicion ? (disposicion.columnas === 6 ? 'grid-cols-3' : 'grid-cols-1') : 'grid-cols-1 xl:grid-cols-3')}
      >
        <MiniHora titulo={t('salaControl.porHora.cvr')} hoy={ph.cvr.hoy} ayer={ph.cvr.ayer} formato={FORMATO_NORMAL.cvr} enPuntos referencia={referencia} paradas={paradasHora} />
        <MiniHora titulo={t('salaControl.porHora.porFan')} hoy={ph.por_fan.hoy} ayer={ph.por_fan.ayer} formato={FORMATO_NORMAL.por_fan} enPuntos={false} referencia={referencia} paradas={paradasHora} />
        <MiniHora titulo={t('salaControl.porHora.porMsg')} hoy={ph.por_msg.hoy} ayer={ph.por_msg.ayer} formato={FORMATO_NORMAL.por_msg} enPuntos={false} referencia={referencia} paradas={paradasHora} />
      </div>
    </>
  );
}

/** Acumulado de una serie por hora (índice h = hasta las h:00); se corta en la primera hora sin dato. */
function acumulada(serie: Array<number | null>): number[] {
  const out = [0];
  for (const v of serie) {
    if (v === null) break;
    out.push(out[out.length - 1]! + v);
  }
  return out;
}

/** 22-10 (Pau): «un botón para filtrar por cada modelo que haya activo […] y que se quite solo cuando el test se cierra».
 *  Los modelos salen del servidor (backend sala/modelos.ts): los que contestan hoy a un trozo de los fans. Sin dos o
 *  más, no hay botones. */
function SelectorModelo({ modelos, elegido, alElegir }: { modelos: Array<{ modelo: string; pct: number }>; elegido: string | null; alElegir: (m: string | null) => void }) {
  const boton = (activo: boolean) =>
    cn(
      'flex min-h-9 items-center gap-1.5 rounded-o-s px-3 text-o-sub transition-colors',
      FOCO,
      activo ? 'bg-oficina-hover font-semibold text-oficina-texto' : 'text-oficina-texto2 hover:text-oficina-texto',
    );
  return (
    <div role="group" aria-label={t('salaControl.modelos.titulo')} className="flex flex-wrap items-center gap-1 self-start rounded-o-m bg-oficina-elevada p-1">
      <button type="button" aria-pressed={elegido === null} onClick={() => alElegir(null)} className={boton(elegido === null)}>
        {t('salaControl.modelos.todos')}
      </button>
      {modelos.map((m) => (
        <button key={m.modelo} type="button" aria-pressed={elegido === m.modelo} onClick={() => alElegir(m.modelo)} className={boton(elegido === m.modelo)}>
          {nombreFamilia(m.modelo)}
          <span className="text-o-nota font-normal text-oficina-texto3">{`${numero(m.pct)} %`}</span>
        </button>
      ))}
    </div>
  );
}

/** Un modelo: sus ventas, sus seis métricas y sus gráficas por hora frente al MISMO modelo ayer a la misma hora. */
function VistaModelo({
  vm,
  horaUtc,
  referencia,
  disposicion,
  paradas,
}: {
  vm: VistaModeloSala;
  horaUtc: number;
  referencia: Date;
  disposicion: DisposicionPulso | null;
  /** Calypso parado: es de todos los modelos a la vez. */
  paradas?: { hoy: number[]; ayer: number[] };
}) {
  const cerradas = Math.floor(horaUtc);
  const acumulado = { hoy: acumulada(vm.porHora.ventas.hoy.slice(0, cerradas)), ayer: acumulada(vm.porHora.ventas.ayer), sem: [] as number[] };
  return (
    <>
      <div className="px-1">
        <p className="mb-1.5 text-o-texto text-oficina-texto2">{t('salaControl.modelos.ventasDe', { modelo: nombreFamilia(vm.modelo) })}</p>
        <div className="flex flex-wrap items-end gap-x-5 gap-y-1">
          <p className="text-o-cifra-grande font-bold leading-none tracking-tight">{dinero(vm.ventas.hoy)}</p>
          <div className="pb-0.5 text-o-cuerpo leading-relaxed text-oficina-texto3">
            <p>
              <Comparacion d={delta(vm.ventas.hoy, vm.ventas.ayer, false)} className="font-medium" /> {t('salaControl.modelos.vsAyer')}
            </p>
            <p>{t('salaControl.modelos.fans', { fans: numero(vm.fans), pct: numero(vm.pct) })}</p>
          </div>
        </div>
        <p data-testid="nota-modelo" className="mt-2 text-o-nota text-oficina-texto3">{t('salaControl.modelos.nota')}</p>
      </div>
      <TarjetasMetricas metricas={vm.metricas} normal={{}} disposicion={disposicion} conSemana={false} />
      <HorasDelDia acumulado={acumulado} horaHoy={cerradas} ph={vm.porHora} referencia={referencia} paradas={paradas} disposicion={disposicion} conSemana={false} />
    </>
  );
}


const METRICAS_DIA: ClaveMetrica[] = ['unlock', 'cvr', 'golden', 'por_fan', 'por_msg'];

/** Un modelo en un periodo de días cerrados (22-10, Pau: «cuando cambio de fecha se quita el filtro»): sus ventas y sus
 *  métricas frente al periodo anterior de la misma duración del MISMO modelo; un día, por horas frente a su día anterior;
 *  varios días, día a día. Sale de la historia por modelo (backend sala/historia/modelos.ts). */
function VistaModeloRango({ m, r, referencia, disposicion }: { m: ModeloRangoSala; r: RangoModelosSala; referencia: Date; disposicion: DisposicionPulso | null }) {
  const periodo = rotulo(r.desde, r.hasta);
  const c = r.comparacion;
  const metricas: MetricaSala[] = (Object.keys(m.b.metricas) as ClaveMetrica[]).map((clave) => ({ clave, hoy: m.b.metricas[clave], ayer: m.a?.metricas[clave] ?? null, sem: null }));
  // Misma regla que «Todos» (backend rango.comparacionDe); con periodos de distinta duración, ventas en media por día.
  const ventasB = c.porDia ? m.b.mediaDia : m.b.ventas;
  const ventasA = m.a ? (c.porDia ? m.a.mediaDia : m.a.ventas) : null;
  const textoComparado = !c.a
    ? t('salaControl.modelos.sinComparar')
    : c.tipo === 'inicio_final'
      ? t('salaControl.modelos.vsInicio', { b: rotulo(c.b.desde, c.b.hasta), a: rotulo(c.a.desde, c.a.hasta) })
      : t('salaControl.modelos.vsAnterior', { periodo: rotulo(c.a.desde, c.a.hasta) });
  const dias = m.serie.map((x) => x.dia);
  return (
    <>
      <div className="px-1">
        <p className="mb-1.5 text-o-texto text-oficina-texto2">{t('salaControl.modelos.ventasPeriodo', { modelo: nombreFamilia(m.modelo), periodo })}</p>
        <div className="flex flex-wrap items-end gap-x-5 gap-y-1">
          <p className="text-o-cifra-grande font-bold leading-none tracking-tight">{dinero(m.total.ventas)}</p>
          <div className="pb-0.5 text-o-cuerpo leading-relaxed text-oficina-texto3">
            <p data-testid="comparacion-modelo">
              {ventasA !== null ? <Comparacion d={delta(ventasB, ventasA, false)} className="font-medium" /> : null} {textoComparado}
            </p>
            <p>{t('salaControl.modelos.fansPeriodo', { fans: numero(m.fans), pct: numero(m.pct) })}</p>
          </div>
        </div>
        <p data-testid="nota-modelo" className="mt-2 text-o-nota text-oficina-texto3">{t('salaControl.modelos.nota')}</p>
        {r.faltan.length ? <p className="mt-1 text-o-nota text-oficina-bloqueado">{t('salaControl.modelos.faltan', { n: r.faltan.length })}</p> : null}
      </div>
      <TarjetasMetricas metricas={metricas} normal={{}} disposicion={disposicion} conSemana={false} etiquetaComparacion={t('salaControl.modelos.anteriorCorto')} />
      {m.porHora ? (
        <HorasDelDia
          acumulado={{ hoy: acumulada(m.porHora.ventas.hoy), ayer: acumulada(m.porHora.ventas.ayer), sem: [] }}
          horaHoy={24}
          ph={m.porHora}
          referencia={referencia}
          disposicion={disposicion}
          conSemana={false}
          titulo={t('salaControl.modelos.horasDia')}
        />
      ) : (
        <HoverDia>
          <div className="grid grid-cols-1 gap-2.5 xl:grid-cols-3">
            <div className="flex min-h-[220px] flex-col rounded-o-m bg-oficina-elevada px-4 pb-2 pt-3 xl:col-span-3">
              <h3 className="mb-1 text-o-texto font-semibold">{t('salaControl.modelos.ventasDia')}</h3>
              <BarrasDia dias={dias} valores={m.serie.map((x) => x.ventas)} formato={(v) => dinero(v)} formatoEje={(v) => `${miles(v)} k$`} etiqueta={t('salaControl.modelos.ventasDia')} />
            </div>
            {METRICAS_DIA.map((k) => (
              <div key={k} className="flex min-h-[180px] flex-col rounded-o-m bg-oficina-elevada px-4 pb-2 pt-3">
                <h3 className="mb-1 text-o-sub text-oficina-texto2">{t(`salaControl.metricas.${k}.nombre`)}</h3>
                <LineaDia dias={dias} valores={m.serie.map((x) => x.metricas[k])} formatoEje={FORMATO_NORMAL[k]} etiqueta={t(`salaControl.metricas.${k}.nombre`)} />
              </div>
            ))}
          </div>
        </HoverDia>
      )}
    </>
  );
}

function VistaTodos({
  pulso,
  referencia,
  hasta,
  r30,
  r7,
  alAbrirDia,
  disposicion,
}: {
  pulso: PulsoSala;
  referencia: Date;
  hasta: string;
  r30: RangoSala | null;
  r7: RangoSala | null;
  alAbrirDia: (d: string) => void;
  disposicion: DisposicionPulso | null;
}) {
  const v = pulso.ventas;
  const ph = pulso.porHora;
  const paradasHora = pulso.paradas ? { hoy: pulso.paradas.hoy, ayer: pulso.paradas.ayer } : undefined;
  // Ayer con Calypso parado antes de esta hora: la comparación con ayer sale mejor de lo que es, y se dice.
  const paradoAyerAntes = tramosDeHoras(pulso.paradas?.ayer ?? []).filter(([a]) => pulso.hora_utc !== null && a < pulso.hora_utc);
  const normal = pulso.historia?.normal ?? {};
  // 10-10 (Pau): «quiero las ganancias esperadas de hoy a este ritmo y ya está» — sustituye a la lectura de hoy.
  const esperado = pulso.historia?.esperado ?? null;
  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-x-7 gap-y-2 px-1">
        <div className="flex flex-wrap items-start gap-x-12 gap-y-3">
          <div>
            {/* 10-10 (Pau): rótulo corto; «hasta las…» ya está en el selector de fechas y el día UTC, al pasar el ratón. */}
            <p className="mb-1.5 text-o-texto text-oficina-texto2" title={t('salaControl.ventasHoyAyuda', { desde: horaUtc(0, referencia), hora: hasta })}>
              {t('salaControl.ventasHoy')}
            </p>
            {/* 10-10 (Pau): las comparaciones van pegadas a la cifra, no detrás del ancho de la etiqueta — así el
                bloque no deja un hueco y las ganancias esperadas caben en la misma fila. */}
            <div className="flex flex-wrap items-end gap-x-5 gap-y-1">
              {v ? (
                <Exacto
                  filas={[
                    { etiqueta: t('salaControl.hoy'), valor: dineroExacto(v.hoy), marca: 'bg-oficina-te-necesita' },
                    { etiqueta: t('salaControl.grafica.ayerAEstaHora'), valor: dineroExacto(v.ayer), marca: 'bg-oficina-texto3', tenue: true },
                    { etiqueta: t('salaControl.grafica.semanaAEstaHora'), valor: dineroExacto(v.sem), marca: 'border-oficina-esperando', forma: 'discontinua', tenue: true },
                  ]}
                >
                  <p className="text-o-cifra-grande font-bold leading-none tracking-tight">{dinero(v.hoy)}</p>
                </Exacto>
              ) : (
                <p className="text-o-cifra-grande font-bold leading-none tracking-tight">{SIN_VALOR}</p>
              )}
              {v ? (
                <div data-testid="ventas-comparaciones" className="pb-0.5 text-o-cuerpo leading-relaxed text-oficina-texto3">
                  <p>
                    <Comparacion d={delta(v.hoy, v.ayer, false)} className="font-medium" /> {t('salaControl.vsAyer')}
                  </p>
                  <p>
                    <Comparacion d={delta(v.hoy, v.sem, false)} className="font-medium" /> {t('salaControl.vsSemana')}
                  </p>
                  {paradoAyerAntes.length ? (
                    <p data-testid="aviso-ayer-parado" className="text-o-sub text-oficina-bloqueado" title={t('salaControl.parado.ayuda')}>
                      {t('salaControl.parado.nota', { tramos: paradoAyerAntes.map(([a, b]) => `${horaUtc(a, referencia)}–${horaUtc(b, referencia)}`).join(', ') })}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
          {esperado ? (
            <div data-testid="esperado-hoy">
              <p className="mb-1.5 text-o-texto text-oficina-texto2">{t('salaControl.esperado.titulo')}</p>
              <p className="text-o-cifra-grande font-bold leading-none tracking-tight text-oficina-texto2">{dinero(esperado.total)}</p>
              {esperado.alto - esperado.bajo >= 1 ? (
                <p className="mt-1.5 text-o-sub text-oficina-texto3" title={t('salaControl.esperado.horquillaAyuda', { bajo: dinero(esperado.bajo), alto: dinero(esperado.alto) })}>
                  {t('salaControl.esperado.horquilla', { bajo: numero(esperado.bajo), alto: dinero(esperado.alto) })}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <TarjetasMetricas metricas={pulso.metricas} normal={normal} disposicion={disposicion} />

      <HorasDelDia acumulado={pulso.acumulado} horaHoy={pulso.hora_utc} ph={ph} referencia={referencia} paradas={paradasHora} disposicion={disposicion} prevision={esperado} />

      <FansHoy pulso={pulso} referencia={referencia} hasta={hasta} r30={r30} r7={r7} alAbrirDia={alAbrirDia} />

      <Cuentas />
    </>
  );
}
